import { useEffect, useMemo, useRef, useState } from 'react';
import { FormInstance } from 'antd';
import { useLazyQuery } from '@apollo/client';
import { IAiEnrichmentResult, IEnrichedField, TAG_FIELDS } from 'lib/Interfaces/IAiEnrichment';
import { analyzeServiceUrl, TAG_KEYS } from 'lib/services/aiEnrichmentService';
import { useNotification } from 'lib/hooks';
import GET_CONSENT_TEMPLATE from 'lib/gql/queries/getConsentTemplate';

type GetConsentTemplateData = {
  getConsentTemplateByDataProcessorOrTemplateId: Array<{
    dataProcessor: string;
    templateId: string;
  }>;
};
type GetConsentTemplateVars = { searchTerm: string };

// Sets for apply-time validation — built once from the same source sent to the backend
const ALLOWED_TAG_VALUES: Record<string, Set<string>> = Object.fromEntries(
  Object.entries(TAG_KEYS).map(([k, v]) => [k, new Set(v)]),
);

function extractDomain(url: string): string {
  try {
    const hostname = new URL(url).hostname;
    // Skip IP addresses — they produce meaningless search terms
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return '';
    // Strip 'www.' and return the base domain name (e.g. "facebook" from "www.facebook.com")
    return hostname.replace(/^www\./, '').split('.')[0];
  } catch {
    return '';
  }
}

export function useAiEnrichment(selectedLanguage: string, editTemplate: boolean | undefined, form: FormInstance) {
  const notification = useNotification();
  const [searchExistingDps] = useLazyQuery<GetConsentTemplateData, GetConsentTemplateVars>(GET_CONSENT_TEMPLATE);

  const [serviceUrl, setServiceUrl] = useState('');
  const [enriching, setEnriching] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const duplicateRunRef = useRef(0);

  const [enrichmentResult, setEnrichmentResult] = useState<IAiEnrichmentResult | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set());
  const [duplicateMatches, setDuplicateMatches] = useState<Array<{ dataProcessor: string; templateId: string }>>([]);

  useEffect(() => {
    if (enriching) {
      setProgress(0);
      progressRef.current = setInterval(() => {
        setProgress((p) => {
          if (p < 30) return p + 5;
          if (p < 70) return p + 2;
          if (p < 92) return p + 0.5;
          return p;
        });
      }, 400);
    } else {
      if (progressRef.current) clearInterval(progressRef.current);
      setProgress((p) => (p > 0 ? 100 : 0));
      timeoutRef.current = setTimeout(() => setProgress(0), 800);
    }
    return () => {
      if (progressRef.current) clearInterval(progressRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [enriching]);

  // When editTemplate is true, hide dataProcessor from the drawer entirely
  const drawerEnrichmentResult = useMemo<IAiEnrichmentResult | null>(() => {
    if (!enrichmentResult || !editTemplate) return enrichmentResult;
    const { dataProcessor: _omitted, ...rest } = enrichmentResult;
    return rest as IAiEnrichmentResult;
  }, [enrichmentResult, editTemplate]);

  const handleAnalyze = async () => {
    let normalizedUrl = serviceUrl.trim();
    if (!/^https?:\/\//i.test(normalizedUrl)) {
      normalizedUrl = `https://${normalizedUrl}`;
    }
    try {
      new URL(normalizedUrl);
    } catch {
      notification.notify({
        notificationMessage: 'Invalid URL. Please enter a valid web address.',
        type: 'error',
      });
      return;
    }

    setDuplicateMatches([]);

    // Fire duplicate check in parallel — don't await, just let it update state when done
    // Track run ID to discard stale results if the user re-analyzes before this resolves
    const thisRun = ++duplicateRunRef.current;
    const domain = extractDomain(normalizedUrl);
    if (domain) {
      searchExistingDps({ variables: { searchTerm: domain } })
        .then(({ data }) => {
          if (duplicateRunRef.current !== thisRun) return;
          const matches = data?.getConsentTemplateByDataProcessorOrTemplateId;
          if (matches && matches.length > 0) {
            const seen = new Set<string>();
            const unique: Array<{ dataProcessor: string; templateId: string }> = [];
            for (const m of matches) {
              if (!seen.has(m.templateId)) {
                seen.add(m.templateId);
                unique.push({ dataProcessor: m.dataProcessor, templateId: m.templateId });
              }
              if (unique.length >= 5) break;
            }
            setDuplicateMatches(unique);
          }
        })
        .catch(() => undefined);
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setEnriching(true);
    try {
      const result = await analyzeServiceUrl(normalizedUrl, selectedLanguage || 'en', controller.signal);

      if (result.scrapingStatus === 'blocked') {
        setEnrichmentResult(null);
        setDrawerOpen(false);
        notification.notify({
          notificationMessage: 'Website blocked automated access. Try the Privacy Policy URL directly.',
          type: 'warning',
        });
        return;
      }
      if (result.scrapingStatus === 'unreachable') {
        setEnrichmentResult(null);
        setDrawerOpen(false);
        notification.notify({
          notificationMessage: 'Could not reach the URL. Please check the address and try again.',
          type: 'error',
        });
        return;
      }

      // Pre-select fields with high confidence, excluding dataProcessor in edit mode
      const filteredResult = editTemplate
        ? (({ dataProcessor: _omit, ...rest }) => rest as IAiEnrichmentResult)(result)
        : result;
      const preSelected = new Set(
        Object.keys(filteredResult).filter((k) => {
          if (k === 'scrapingStatus') return false;
          const field = filteredResult[k as keyof IAiEnrichmentResult];
          return field && typeof field === 'object' && (field.confidence === undefined || field.confidence >= 0.7);
        }),
      );
      setEnrichmentResult(result);
      setSelectedFields(preSelected);

      if (result.scrapingStatus === 'partial') {
        notification.notify({
          notificationMessage: 'Some pages could not be accessed — suggestions may be incomplete.',
          type: 'warning',
        });
      }
      setDrawerOpen(true);
    } catch (err) {
      console.error('[useAiEnrichment] analyzeServiceUrl failed:', err);
      notification.notify({
        notificationMessage: 'Could not analyze the URL. Please check the address and try again.',
        type: 'error',
      });
    } finally {
      setEnriching(false);
    }
  };

  const applySelected = () => {
    if (!drawerEnrichmentResult) return;
    const patch: Record<string, string | string[]> = {};
    for (const fieldName of Array.from(selectedFields)) {
      const suggestion = drawerEnrichmentResult[fieldName as keyof IAiEnrichmentResult] as
        | IEnrichedField
        | null
        | undefined;
      if (!suggestion || typeof suggestion === 'string') continue;
      if (TAG_FIELDS.has(fieldName)) {
        const allowed = ALLOWED_TAG_VALUES[fieldName];
        patch[fieldName] = allowed ? suggestion.value.filter((v) => allowed.has(v)) : suggestion.value;
      } else {
        patch[fieldName] = suggestion.value[0];
      }
    }
    form.setFieldsValue(patch);
    setDrawerOpen(false);
  };

  const onToggleField = (field: string) =>
    setSelectedFields((prev) => {
      const next = new Set(prev);
      if (next.has(field)) {
        next.delete(field);
      } else {
        next.add(field);
      }
      return next;
    });

  const onSelectAll = () =>
    setSelectedFields(
      new Set(
        Object.keys(drawerEnrichmentResult ?? {}).filter(
          (k) => k !== 'scrapingStatus' && drawerEnrichmentResult?.[k as keyof IAiEnrichmentResult] != null,
        ),
      ),
    );

  const onDeselectAll = () => setSelectedFields(new Set());

  return {
    serviceUrl,
    setServiceUrl,
    enriching,
    progress,
    drawerEnrichmentResult,
    drawerOpen,
    setDrawerOpen,
    selectedFields,
    handleAnalyze,
    applySelected,
    onToggleField,
    onSelectAll,
    onDeselectAll,
    duplicateMatches,
  };
}
